import axios from "axios";
import { useEffect, useState } from "react";
import Box from "elements/Box";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { useSnackbar } from "notistack";
import DateRange from "elements/DateRange";
import moment from "moment";
import ReportViewer from "./ReportViewer";
import { ApiRequest } from "GlobalFunctions";
import EventIcon from '@mui/icons-material/Event';
import AutocompleteReportUsers from "elements/AutocompleteReportUsers";
import AutocompleteField from "elements/AutocompleteField";

export default function OrdersReports(props: any) {
  const { setLoading } = props;

  const [loadingPopup, setLoadingPopup] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    setLoading(false);
  }, []);

  const defaultValues: any = {
    date: null,
    user_id: null,
    client_id: null,
    payment_method_id: null,
    processed: null,
    label_type: null,
    warehouse_id: null,
  };

  const [exportType, setExportType] = useState("");
  const [formValues, setFormValues] = useState({ ...defaultValues });
  const [reportName, setReportName] = useState("");

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  const [progress, setProgress] = useState(0);
  const [fileUrl, setFileUrl] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [data, setData] = useState([]);
  const [openViewer, setOpenViewer] = useState(false);

  const getReport = async (download = "") => {
    setLoading(true);
    setLoadingPopup(true);

    //if report is byClient, verify if client is selected
    if(exportType === "byClient" && !formValues.client_id){
      enqueueSnackbar("Debes seleccionar un cliente", { variant: 'error' });
      setLoading(false);
      setLoadingPopup(false);
      return;
    }

    //if report is orders_processed, verify if processed status is selected
    if(exportType === "orders_processed" && formValues.processed === null){
      enqueueSnackbar("Debes seleccionar el estado de procesamiento", { variant: 'error' });
      setLoading(false);
      setLoadingPopup(false);
      return;
    }

    //if report is sales_by_product_and_label, verify if label_type is selected
    if(exportType === "sales_by_product_and_label" && !formValues.label_type){
      enqueueSnackbar("Debes seleccionar una etiqueta", { variant: 'error' });
      setLoading(false);
      setLoadingPopup(false);
      return;
    }

    //if report is purchased_vs_received, verify if warehouse is selected
    if(exportType === "purchased_vs_received" && !formValues.warehouse_id){
      enqueueSnackbar("Debes seleccionar una bodega", { variant: 'error' });
      setLoading(false);
      setLoadingPopup(false);
      return;
    }

    await ApiRequest({
      url: "/reports/getReport",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: {
        ...formValues,
        exportType,
        module: "purchases_orders",
        download
      },
      setResponse: (response: any) => {
        console.log("response", response);
        if(response.status === "success"){
          setData(response.data);
          setOpenViewer(true);

          if(download === 'pdf') {
            console.log(response.pdfUrl);
            window.open(response.pdfUrl, '_blank');
          }
          if(download === 'excel'){
            window.open(response.fileUrl, '_blank');
          }
        }
        else{
          enqueueSnackbar(response.message, { variant: 'error' });
        }
        setLoading(false);
        setLoadingPopup(false);
      },
    });
  };

  return (
    <>
      <div id="SupplierList" className="ModuleContainer">
        <div className="ModuleWrapper">
          <div className="boxContainerHeader">
            <div className="boxContainerTitle">
              <h2 className="h2_title">Reportes Abastecimiento</h2>
              <span className="h2_subtitle">Selecciona el tipo de exportación</span>
            </div>
          </div>

          <Box id="SupplierListIn" className="box100">

            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Reporte</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                value={exportType}
                label="Reporte"
                name="export_type"
                onChange={(e: any) => {
                  setExportType(e.target.value);
                  const newFormValues = e.target.value === "orders_processed" 
                    ? { ...defaultValues, processed: 1 }
                    : { ...defaultValues };
                  setFormValues(newFormValues);
                  setReportName(e.target.value);
                }}
              >
                <MenuItem value={"byUser"}>Ventas por usuario</MenuItem>
                <MenuItem value={"orders_range"}>Pedidos</MenuItem>
                <MenuItem value={"new_clients"}>Nuevos Clientes</MenuItem>
                <MenuItem value={"updated_clients"}>Clientes Actualizados</MenuItem>
                <MenuItem value={"orders"}>Pedidos (móvil)</MenuItem>
                <MenuItem value={"ordersWithMessage"}>Pedidos con mensaje</MenuItem>
                <MenuItem value={"ineffective_visits"}>Visitas sin compra</MenuItem>
                <MenuItem value={"sales_by_product"}>Ventas por producto</MenuItem>
                <MenuItem value={"sales_by_product_and_label"}>Ventas por etiqueta</MenuItem>
                <MenuItem value={"sales_by_supplier"}>Ventas por proveedor</MenuItem>
                <MenuItem value={"clients_by_product"}>Clientes por producto</MenuItem>
                <MenuItem value={"clients_by_supplier"}>Clientes por proveedor</MenuItem>
                <MenuItem value={"returns_novelties"}>Reporte devoluciones y/o novedades</MenuItem>
                <MenuItem value={"assigned_assets"}>Activos asignados</MenuItem>
                {/* New report: Novedades de activos */}
                <MenuItem value={"assets_novelties"}>Novedades de activos</MenuItem>
                {/* New report: Orders processed */}
                <MenuItem value={"orders_processed"}>Pedidos procesados</MenuItem>
                <MenuItem value={"purchased_vs_received"}>Comprado vs Recibido</MenuItem>
              </Select>
            </FormControl>

            {exportType && exportType !== "orders_processed" && (
              (exportType === "byUser" || exportType === "orders_range" ||
               exportType === "new_clients" || exportType === "updated_clients" ||
                exportType === "orders" || exportType === "ordersWithMessage" || exportType === "ineffective_visits" ||
                exportType === "sales_by_product" || exportType === "sales_by_product_and_label" || exportType === "sales_by_supplier" ||
                exportType === "clients_by_product" || exportType === "clients_by_supplier" ||
                exportType === "returns_novelties" ||
                exportType === "assigned_assets" ||
                exportType === "assets_novelties" ||
                exportType === "purchased_vs_received"
              ) ? (
                <DateRange
                  className="filters_datePicker"
                  label="Date range"
                  variant="outlined"
                  fullWidth
                  value={{
                    startDate: formValues.date?.startDate || null,
                    endDate: formValues.date?.endDate || null,
                  }}
                  format="MMMM DD, yyyy"
                  onChange={(e: any) => {
                    if (e.startDate && e.endDate) {
                      const startDate = moment(e.startDate).format("YYYY-MM-DD");
                      const endDate = moment(e.endDate).format("YYYY-MM-DD");

                      setFormValues({
                        ...formValues,
                        date: { startDate, endDate },
                      });
                    }
                  }}
                  variantDatePicker="side-by-side"
                />
              ) : (
                // For mobile reports that use date_range format instead
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <MobileDatePicker
                  label="Fecha"
                  inputFormat="MMMM DD, yyyy"
                  value={formValues.date}
                  InputProps={{
                      endAdornment: (
                      <EventIcon />
                      ),
                  }}
                  showToolbar={false}
                  closeOnSelect 
                  onChange={(newValue:any) => {
                      // Format for mobile reports: 7 days before and current date
                      const endDate = moment(newValue).format('YYYY-MM-DD');
                      const startDate = moment(newValue).subtract(7, 'days').format('YYYY-MM-DD');
                      const date_range = `${startDate}/${endDate}`;
                      
                      setFormValues({
                          ...formValues,
                          date: newValue,
                          date_range: date_range
                      });
                  }}
                  renderInput={(params) => <TextField {...params} />}
                  />
              </LocalizationProvider>
              )
            )}

            {formValues.date && (
              exportType === "byUser"
            ) 
            && (
              <AutocompleteField
                label="Usuario"
                endpoint="/users/listByRole"
                endpoint_params={{ 
                  role_ids: [13],
                  full_list: true
                }}
                value={formValues.user_id}
                onChange={(data: any) => {
                  setFormValues({...formValues, user_id: data?.value || null});
                }}
              />
            )}

            {/* Add user selection for mobile reports and orders_range */}
            {formValues.date && (
              exportType === "orders_range" ||
              exportType === "orders" ||
              exportType === "ordersWithMessage" ||
              exportType === "ineffective_visits" ||
              exportType === "sales_by_product" ||
              exportType === "sales_by_supplier" ||
              exportType === "clients_by_product" ||
              exportType === "clients_by_supplier" ||
              exportType === "assigned_assets" ||
              exportType === "assets_novelties"
            ) 
            && (
              <AutocompleteField
                label="Usuario"
                endpoint="/users/listByRole"
                endpoint_params={{ 
                  role_ids: [13],
                  full_list: true
                }}
                value={formValues.user_id}
                onChange={(data: any) => {
                  setFormValues({...formValues, user_id: data?.value || null});
                }}
              />
            )}

            {/* Product selection for product-specific reports */}
            {formValues.date && (
              exportType === "sales_by_product" || exportType === "clients_by_product"
            ) 
            && (
              <AutocompleteField
                label="Producto"
                endpoint="/products/list"
                endpoint_params={{ full_list: true }}
                value={formValues.product_id}
                onChange={(data: any) => {
                  setFormValues({...formValues, product_id: data?.value || null});
                }}
              />
            )}

            {/* Label selection for label-specific reports */}
            {formValues.date && exportType === "sales_by_product_and_label" && (
              <FormControl fullWidth required>
                <InputLabel id="label-type-select-label">Etiqueta</InputLabel>
                <Select
                  labelId="label-type-select-label"
                  id="label-type-select"
                  value={formValues.label_type || ''}
                  label="Etiqueta"
                  name="label_type"
                  onChange={(e: any) => {
                    setFormValues({...formValues, label_type: e.target.value});
                  }}
                >
                  <MenuItem value="new">Nuevos</MenuItem>
                  <MenuItem value="recommended">Recomendados</MenuItem>
                  <MenuItem value="promo">Promoción</MenuItem>
                </Select>
              </FormControl>
            )}

            {/* Supplier selection for supplier-specific reports */}
            {formValues.date && (
              exportType === "sales_by_supplier" || exportType === "clients_by_supplier"
            )
            && (
              <AutocompleteField
                label="Proveedor"
                endpoint="/suppliers/list"
                endpoint_params={{ full_list: true }}
                value={formValues.supplier_id}
                onChange={(data: any) => {
                  setFormValues({...formValues, supplier_id: data?.value || null});
                }}
              />
            )}

            {/* Warehouse selector for purchased_vs_received report */}
            {formValues.date && exportType === "purchased_vs_received" && (
              <AutocompleteField
                label="Bodega"
                endpoint="/warehouses/list"
                value={formValues.warehouse_id}
                onChange={(data: any) => {
                  setFormValues({...formValues, warehouse_id: data?.value || null});
                }}
              />
            )}

            {/* Processed status selector for orders_processed report */}
            {exportType === "orders_processed" && (
              <FormControl fullWidth required>
                <InputLabel id="processed-select-label">Estado de procesamiento</InputLabel>
                <Select
                  labelId="processed-select-label"
                  id="processed-select"
                  value={formValues.processed !== null ? formValues.processed : "1"}
                  label="Estado de procesamiento"
                  required
                  onChange={(e: any) => {
                    setFormValues({...formValues, processed: e.target.value});
                  }}
                >
                  <MenuItem value={1}>Procesados</MenuItem>
                  <MenuItem value={0}>Sin procesar</MenuItem>
                </Select>
              </FormControl>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                getReport();
              }}
              startIcon={<CloudDownloadOutlinedIcon />}
            >
              Ver
            </Button>

          </Box>
        </div>
      </div>

      <ReportViewer
        open={openViewer}
        setOpen={setOpenViewer}
        data={data}
        loading={loadingPopup}
        setLoading={setLoading}
        reportName={reportName}
        getReport={getReport}
        processed={formValues.processed}
        label_type={formValues.label_type}
      />
    </>
  );
}
